import React, { useRef, useEffect, useCallback } from "react";

import { Form } from "react-router-dom";
import { Formik } from "formik";
import { Box } from "@mui/material";

import InputField from "../InputField";
import { Paragraph } from "../../../../types";

interface SubtitleModeSwitcherProps {
  paragraphs: Paragraph[];
  onUpdateParagraph: (paragraph: Paragraph) => void;
  currentTimeInMilliseconds: number;
  onParagraphClick: (paragraph: Paragraph) => void;
}

export default function SubtitlesEditor({
  paragraphs,
  onUpdateParagraph,
  currentTimeInMilliseconds,
  onParagraphClick,
}: SubtitleModeSwitcherProps) {
  const inputRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const lastScrolledParagraphRef = useRef<string | null>(null);

  const currentTimeInSeconds = currentTimeInMilliseconds / 1000;

  const currentParagraph = paragraphs.find(
    (p) => p.start <= currentTimeInSeconds && currentTimeInSeconds <= p.end,
  );

  // Debounced scroll function to prevent ResizeObserver loops
  const debouncedScrollIntoView = useCallback((element: HTMLDivElement) => {
    // Use requestAnimationFrame to defer the scroll operation
    requestAnimationFrame(() => {
      // Use a timeout to further defer and prevent loops
      setTimeout(() => {
        if (element && element.scrollIntoView) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
        }
      }, 16); // One frame delay
    });
  }, []);

  useEffect(() => {
    if (currentParagraph) {
      const key = currentParagraph.start.toString();

      // Prevent unnecessary scrolls to the same paragraph
      if (lastScrolledParagraphRef.current === key) {
        return;
      }

      const currentElement = inputRefs.current[key];
      if (currentElement) {
        lastScrolledParagraphRef.current = key;
        debouncedScrollIntoView(currentElement);
      }
    }
  }, [currentParagraph, debouncedScrollIntoView]);

  const handleUpdateText = (text: string, paragraphStartsAt: string) => {
    const paragraph = paragraphs.find(
      (p) => p.start === Number(paragraphStartsAt),
    );
    if (paragraph) {
      onUpdateParagraph({
        ...paragraph,
        text,
      });
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Formik
        enableReinitialize
        initialValues={paragraphs?.reduce((acc, paragraph) => {
          return {
            ...acc,
            [paragraph.start]: paragraph?.text,
          };
        }, {})}
        onSubmit={() => {}}
      >
        {({ values, handleSubmit }) => (
          <Form onSubmit={handleSubmit}>
            {Object.keys(values)?.map((key) => {
              const paragraph = paragraphs.find((p) => p.start === Number(key));
              const isCurrentParagraph =
                paragraph &&
                paragraph.start <= currentTimeInSeconds &&
                currentTimeInSeconds <= paragraph.end;

              return (
                <InputField
                  key={key}
                  ref={(el) => {
                    inputRefs.current[key] = el;
                  }}
                  name={key}
                  values={values}
                  onUpdateText={(text: string) => {
                    handleUpdateText(text, key);
                  }}
                  isCurrentParagraph={!!isCurrentParagraph}
                  onParagraphClick={() => {
                    if (paragraph) {
                      onParagraphClick(paragraph);
                    }
                  }}
                />
              );
            })}
          </Form>
        )}
      </Formik>
    </Box>
  );
}
